/**
 * Users Page Constants
 *
 * This file contains all constants used in the Users page
 * including role options, badge colors, and table configurations.
 */

// Role options for filter dropdown
export const USER_ROLE_OPTIONS = [
  { value: '', labelKey: 'users.allRoles' },
  { value: 'super_admin', labelKey: 'users.roleSuperAdmin' },
  { value: 'owner', labelKey: 'users.roleOwner' },
  { value: 'user', labelKey: 'users.roleUser' },
]

// Role options for edit modal (without "All Roles")
export const USER_ROLE_EDIT_OPTIONS = [
  { value: 'user', labelKey: 'users.roleUser' },
  { value: 'owner', labelKey: 'users.roleOwner' },
  { value: 'super_admin', labelKey: 'users.roleSuperAdmin', requiresSuperAdmin: true },
]

// Role badge color classes (legacy - kept for backward compatibility)
export const USER_ROLE_BADGE_COLORS = {
  super_admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  owner: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  user: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  default: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
}

// Role badge variants for Badge component
export const USER_ROLE_BADGE_VARIANTS = {
  super_admin: 'purple',
  owner: 'orange',
  user: 'green',
  default: 'gray',
}

// Role colors for HLTag component
export const USER_ROLE_HLTAG_COLORS = {
  super_admin: 'purple',
  owner: 'warning',
  user: 'success',
  default: 'gray',
}

// Team and Project HLTag colors (matching dashboard stat card colors)
export const TEAM_HLTAG_COLOR = 'warning' // yellow (matches TEAMS stat card)
export const PROJECT_HLTAG_COLOR = 'blue' // blue (matches PROJECTS stat card)

// Status HLTag color (for active users)
export const STATUS_HLTAG_COLOR = 'success' // green (matches active status)

// Status badge colors
export const USER_STATUS_BADGE_COLORS = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  inactive: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  suspended: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
}

// Team chip colors
export const USER_TEAM_CHIP_COLORS = {
  team: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  more: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
}

// Project chip colors
export const USER_PROJECT_CHIP_COLORS = {
  project: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  more: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
}

// Selected item chip colors for modal
export const SELECTED_ITEM_COLORS = {
  team: {
    background: 'bg-green-100 dark:bg-green-900',
    text: 'text-green-800 dark:text-green-200',
    hover: 'hover:text-green-600 dark:hover:text-green-300',
    border: 'border-green-200 dark:border-green-800',
    checkbox: 'text-green-600 focus:ring-green-500',
  },
  project: {
    background: 'bg-blue-100 dark:bg-blue-900',
    text: 'text-blue-800 dark:text-blue-200',
    hover: 'hover:text-blue-600 dark:hover:text-blue-300',
    border: 'border-blue-200 dark:border-blue-800',
    checkbox: 'text-blue-600 focus:ring-blue-500',
  },
}

// Table column keys
export const USER_TABLE_COLUMNS = {
  USER: 'user',
  ROLE: 'role',
  TEAMS: 'teams',
  PROJECTS: 'projects',
  LAST_ACTIVE: 'lastActive',
  STATUS: 'status',
  ACTIONS: 'actions',
}

// Default permissions by role
export const DEFAULT_PERMISSIONS_BY_ROLE = {
  super_admin: [
    'users:read',
    'users:write',
    'users:delete',
    'users:manage',
    'users:manage_roles',
    'teams:read',
    'teams:write',
    'teams:delete',
    'projects:read',
    'projects:write',
    'projects:delete',
    'projects:create_private',
    'test_cases:read',
    'test_cases:write',
    'test_cases:delete',
    'test_runs:read',
    'test_runs:write',
    'test_runs:delete',
    'test_runs:execute',
    'bugs:read',
    'bugs:write',
    'bugs:delete',
    'bugs:close',
    'reports:read',
    'reports:write',
    'reports:delete',
    'folders:read',
    'folders:write',
    'folders:delete',
    'comments:read',
    'comments:write',
    'comments:delete',
    'features:create',
    'features:read',
    'features:write',
    'features:delete',
    'settings:read',
    'settings:write',
    'learning_hub:read',
    'learning_hub:write',
    'learning_hub:delete',
  ],
  owner: [
    'users:read',
    'users:manage',
    'teams:read',
    'projects:read',
    'projects:write',
    'projects:delete',
    'test_cases:read',
    'test_cases:write',
    'test_cases:delete',
    'test_runs:read',
    'test_runs:write',
    'test_runs:delete',
    'test_runs:execute',
    'bugs:read',
    'bugs:write',
    'bugs:delete',
    'bugs:close',
    'reports:read',
    'folders:read',
    'folders:write',
    'folders:delete',
    'comments:read',
    'comments:write',
    'comments:delete',
    'features:create',
    'features:read',
    'features:write',
    'features:delete',
    'settings:read',
    'learning_hub:read',
    'learning_hub:write',
  ],
  user: [
    'accessibility:read',
    'accessibility:write',
    'bugs:read',
    'bugs:write',
    'comments:read',
    'comments:write',
    'features:create',
    'features:read',
    'folders:read',
    'folders:write',
    'folders:delete',
    'projects:read',
    'reports:read',
    'settings:read',
    'teams:read',
    'test_cases:read',
    'test_cases:write',
    'test_runs:read',
    'test_runs:execute',
    'users:read',
    'learning_hub:read',
    'learning_hub:write',
  ],
}

export const getDefaultPermissionsByRole = (role: string) => {
  const sanitizedRole = role.trim().toLowerCase().replace('-', '_')
  const permissions = DEFAULT_PERMISSIONS_BY_ROLE[sanitizedRole] || []
  return Array.from(new Set([...permissions, ...DEFAULT_PERMISSIONS_BY_ROLE.user]))
}

// Permissions available for manual grant/revoke in Users UI
export const USER_PERMISSION_OPTIONS = [
  'users:read',
  'users:write',
  'users:delete',
  'users:manage_roles',
  'teams:read',
  'teams:write',
  'teams:delete',
  'projects:read',
  'projects:write',
  'projects:delete',
  'projects:create_private',
  'test_cases:read',
  'test_cases:write',
  'test_cases:delete',
  'test_runs:read',
  'test_runs:write',
  'test_runs:delete',
  'test_runs:execute',
  'bugs:read',
  'bugs:write',
  'bugs:delete',
  'bugs:close',
  'reports:read',
  'reports:write',
  'reports:delete',
  'folders:read',
  'folders:write',
  'folders:delete',
  'comments:read',
  'comments:write',
  'comments:delete',
  'settings:read',
  'settings:write',
  'features:create',
  'features:read',
  'features:write',
  'features:delete',
]

// UI Configuration
export const USER_UI_CONFIG = {
  // Maximum items to display before showing "more"
  MAX_VISIBLE_TEAMS: 2,
  MAX_VISIBLE_PROJECTS: 2,

  // Avatar configuration
  AVATAR_SIZE: 8, // h-8 w-8 (reduced from 10)
  AVATAR_DEFAULT_LETTER: 'U',

  // Modal configuration
  TEAM_PROJECT_PANEL_HEIGHT: 48, // h-48 (reduced for compact height)
  TEAM_PROJECT_LIST_HEIGHT: 40, // max-h-40 (reduced for compact height)
  SEARCH_INPUT_DEBOUNCE: 300, // ms
  MODAL_SIZE: '2xl', // 2xl (balanced width for compact design)
  MODAL_CUSTOM_WIDTH: '900px', // Custom width for edit user modal (narrower than 2xl's 672px)

  // Filter configuration
  FILTER_COLUMNS_MD: 4, // grid-cols-1 md:grid-cols-4

  // Grid configurations
  MODAL_GRID_COLS: {
    base: 1,
    lg: 3, // User info: 1 col, Role: 2 cols
  },
  TEAM_PROJECT_GRID_COLS: {
    base: 1,
    lg: 2,
  },
}

// Helper function to get role badge class (legacy)
export function getRoleBadgeClass(role) {
  if (!role) return USER_ROLE_BADGE_COLORS.default
  const normalizedRole = role.toLowerCase()
  return USER_ROLE_BADGE_COLORS[normalizedRole] || USER_ROLE_BADGE_COLORS.default
}

// Helper function to get role badge variant for Badge component
export function getRoleBadgeVariant(role) {
  if (!role) return USER_ROLE_BADGE_VARIANTS.default
  const normalizedRole = role.toLowerCase()
  return USER_ROLE_BADGE_VARIANTS[normalizedRole] || USER_ROLE_BADGE_VARIANTS.default
}
